import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { SearchableMultiSelect } from '@/components/ui/searchable-multiselect'
import { useAuthStore } from '@/store/auth.store'
import { contractService } from '@/services/contractService'
import { gosiService } from '@/services/gosiService'
import {
  Contract,
  EmployeeOption,
  GradeOption,
  ProjectOption,
} from '@/api/types'
import type { GosiVm } from '@/api/types/gosi.types'

/**
 * Contract Detail/Form Page
 * Form for creating new contracts or editing existing ones
 */
export const ContractsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId
  const listEmpId = useMemo(() => {
    const n = Number(searchParams.get('empId') ?? searchParams.get('EmpId') ?? 0)
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])
  const backToList = useCallback(
    (empId?: number) => {
      const idToUse = empId && empId > 0 ? empId : listEmpId
      navigate(idToUse ? `/contracts?empId=${idToUse}` : '/contracts')
    },
    [navigate, listEmpId]
  )

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [renewing, setRenewing] = useState(false)
  const [contract, setContract] = useState<Contract>({
    id: 0,
    empId: 0,
    projectId: 0,
    empDepartmetId: 0,
    gradeId: 0,
    leavePolicyAccessible: false,
    contractMonths: 0,
    contractStartDate: '',
    contractEndDate: '',
    contractDate: '',
    contractstatus: 0,
    allowanceBasedOn: 0,
    transpotationAllowancePercent: 0,
    housingAllowancePercent: 0,
    mealAllowancePercent: 0,
    otherAllowancePercent: 0,
    leavesAvailed: 0,
    leavesBalance: 0,
    casualLeavesAvailed: 0,
    casualLeavesBalance: 0,
    sickLeavesAvailed: 0,
    sickLeavesBalance: 0,
    isRenewal: 0,
    renewalStartDate: '',
    renewalEndDate: '',
    annualLeavesEntitlement: 0,
    leaveAccuralRate: 0,
    casualLeavesEntitlement: 0,
    sickLeavesEntitlement: 0,
    probationPeriod: 0,
    workingDays: 0,
    additionalJob: '',
    basicSalary: 0,
    transpotationAllowance: 0,
    housingAllowance: 0,
    mealAllowance: 0,
    otherAllowance: 0,
    incrementPercent: 0,
    incrementDate: '',
    incrementStatus: 0,
    branchId: branchId || 0,
    totalContractSalary: 0,
    gosiDeduction: 0,
    gosiType: [],
    additionalJobSalary: 0,
    additionalJobExpiry: '',
    otherDeductions: 0,
    basicSalaryDebit: 0,
    housingAllowanceDebit: 0,
    transportationAllowanceDebit: 0,
    mealAllowanceDebit: 0,
    otherAllowanceDebit: 0,
    salaryAllowancesCredit: 0,
    deductionsCredit: 0,
  })

  const hasContractId = isEditMode || (contract.id != null && Number(contract.id) > 0)
  const isContractExpired =
    !!contract.contractEndDate &&
    (() => {
      const end = new Date(contract.contractEndDate!)
      end.setHours(0, 0, 0, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return end.getTime() <= today.getTime()
    })()
  const showRenewButton = hasContractId && isContractExpired

  // Lookup data
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  
  // Debug: Log employees state changes
  useEffect(() => {
    console.log('🔵 [ContractDetail] Employees state changed:', employees.length, 'employees')
    if (employees.length > 0) {
      console.log('🔵 [ContractDetail] First employee in state:', employees[0])
    }
  }, [employees])
  const [grades, setGrades] = useState<GradeOption[]>([])
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [statusOptions, setStatusOptions] = useState<{ id: number; name: string }[]>([])
  const [gosiSettings, setGosiSettings] = useState<GosiVm | null>(null)
  const [leaveBalances, setLeaveBalances] = useState<{
    empId: number
    branchId: number
    sick?: { entitlement?: number; availed?: number; balance?: number }
    casual?: { entitlement?: number; availed?: number; balance?: number }
    annual?: { accrualRate?: number; serviceDays?: number; entitlement?: number; availed?: number; balance?: number }
  } | null>(null)

  // When opening from employee-scoped list, auto-select employee and lock it.
  useEffect(() => {
    if (isEditMode) return
    if (!listEmpId) return
    setContract((prev) => (prev.empId === listEmpId ? prev : { ...prev, empId: listEmpId }))
  }, [isEditMode, listEmpId])

  // Load GOSI settings for deduction calculation
  useEffect(() => {
    if (!branchId) return
    gosiService
      .getGosi(branchId)
      .then((data) => setGosiSettings(data || null))
      .catch(() => setGosiSettings(null))
  }, [branchId])

  // Calculate Leave Accrual Rate: (annual + sick + casual) / 12 (per month)
  useEffect(() => {
    if (contract.leavePolicyAccessible) return
    const annual = Number(contract.annualLeavesEntitlement) || 0
    const sick = Number(contract.sickLeavesEntitlement) || 0
    const casual = Number(contract.casualLeavesEntitlement) || 0
    const months = Number(contract.contractMonths) || 12
    const total = annual + sick + casual
    const rate = months > 0 ? Math.round((total / months) * 100) / 100 : 0
    setContract((prev) => ({ ...prev, leaveAccuralRate: rate }))
  }, [
    contract.leavePolicyAccessible,
    contract.annualLeavesEntitlement,
    contract.sickLeavesEntitlement,
    contract.casualLeavesEntitlement,
    contract.contractMonths,
  ])

  // Calculate GOSI deduction using formula:
  // GOSI = (sum of percentages of gosi included in contract / 100) * (basic salary + allowances and acting job included)
  useEffect(() => {
    if (!gosiSettings) return
    const types = contract.gosiType ?? []
    if (types.length === 0) {
      setContract((prev) => ({ ...prev, gosiDeduction: 0 }))
      return
    }
    // Step 1: Sum of percentages of gosi types included in contract
    let sumOfPercentages = 0
    if (types.includes(1)) sumOfPercentages += gosiSettings.socialSecurityPercent ?? 0
    if (types.includes(2)) sumOfPercentages += gosiSettings.addSocialSecurityPercent ?? 0
    if (types.includes(3)) sumOfPercentages += gosiSettings.foreignSocialSecurityPercent ?? 0
    
    // Step 2: Calculate base amount (basic salary + allowances and acting job included)
    // Only include allowances that are enabled in GOSI setting
    const includeTransport = gosiSettings.isTransportationAllowance !== false
    const includeHousing = gosiSettings.isHousingAllowance !== false
    const includeMeal = gosiSettings.isMealAllowance !== false
    const includeAdditionalJob = gosiSettings.isAdditionalJob !== false
    
    const baseAmount =
      (Number(contract.basicSalary) || 0) +
      (includeTransport ? (Number(contract.transpotationAllowance) || 0) : 0) +
      (includeHousing ? (Number(contract.housingAllowance) || 0) : 0) +
      (includeMeal ? (Number(contract.mealAllowance) || 0) : 0) +
      (includeAdditionalJob ? (Number(contract.additionalJobSalary) || 0) : 0)
    
    // Step 3: Apply formula: GOSI = (sum of percentages / 100) * base amount
    const deduction = Math.round((sumOfPercentages / 100) * baseAmount * 100) / 100
    setContract((prev) => ({ ...prev, gosiDeduction: deduction }))
  }, [
    gosiSettings,
    contract.gosiType,
    contract.basicSalary,
    contract.transpotationAllowance,
    contract.housingAllowance,
    contract.mealAllowance,
    contract.additionalJobSalary,
  ])

  // Load lookup data
  useEffect(() => {
    if (!branchId) {
      console.warn('BranchId not available, cannot load contract lookups')
      console.warn('User object:', user)
      console.warn('Selected branch:', user?.selectedBranch)
      return
    }

    const loadLookups = async () => {
      try {
        setLoading(true)
        console.log('Loading contract lookups for branchId:', branchId)
        console.log('User selectedBranch:', user?.selectedBranch)
        
        const [employeesData, gradesData, projectsData] = await Promise.all([
          contractService.getAllEmployees(branchId),
          contractService.getGrades(),
          contractService.getProjects(branchId),
        ])

        console.log('Loaded lookup data:', {
          employees: employeesData?.length || 0,
          grades: gradesData?.length || 0,
          projects: projectsData?.length || 0,
        })
        console.log('🔵 [ContractDetail] Employees data sample:', employeesData?.[0])
        console.log('🔵 [ContractDetail] All employees:', employeesData)
        console.log('🔵 [ContractDetail] Employees count:', employeesData?.length || 0)

        if (employeesData && employeesData.length > 0) {
          console.log('✅ [ContractDetail] Setting employees state with', employeesData.length, 'employees')
          setEmployees(employeesData)
        } else {
          console.warn('⚠️ [ContractDetail] No employees data to set, employeesData:', employeesData)
          setEmployees([])
        }
        setGrades(gradesData || [])
        setProjects(projectsData || [])

        setStatusOptions([
          { id: 1, name: 'Active' },
          { id: 0, name: 'Not Active' },
        ])
      } catch (error: any) {
        console.error('Failed to load lookups', error)
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url,
        })
        // Don't show toast for individual failures, just log them
        // toast.error(error.response?.data?.message || 'Failed to load lookup data')
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load contract data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadContract = async () => {
      try {
        setLoading(true)
        const contractData = await contractService.getContractById(Number(id), branchId)
        
        setContract({
          ...contractData,
          branchId: branchId,
        })

        if (contractData?.leavePolicyAccessible && contractData?.empId) {
          const lb = await contractService.getLeaveBalances(contractData.empId, branchId)
          setLeaveBalances(lb)
          if (lb) {
            setContract((prev) => ({
              ...prev,
              sickLeavesEntitlement: lb.sick?.entitlement ?? prev.sickLeavesEntitlement,
              sickLeavesAvailed: lb.sick?.availed ?? prev.sickLeavesAvailed,
              sickLeavesBalance: lb.sick?.balance ?? prev.sickLeavesBalance,
              casualLeavesEntitlement: lb.casual?.entitlement ?? prev.casualLeavesEntitlement,
              casualLeavesAvailed: lb.casual?.availed ?? prev.casualLeavesAvailed,
              casualLeavesBalance: lb.casual?.balance ?? prev.casualLeavesBalance,
              annualLeavesEntitlement: lb.annual?.entitlement ?? prev.annualLeavesEntitlement,
              leavesAvailed: lb.annual?.availed ?? prev.leavesAvailed,
              leavesBalance: lb.annual?.balance ?? prev.leavesBalance,
              leaveAccuralRate: lb.annual?.accrualRate ?? prev.leaveAccuralRate,
            }))
          }
        } else {
          setLeaveBalances(null)
        }

        // Load salary data if empId and contractId are available
        if (contractData.empId && contractData.id) {
          try {
            const salaryData = await contractService.getSalaryByEmployeeId(contractData.empId, contractData.id)
            if (salaryData) {
              console.log('🔵 [ContractDetail] Salary data loaded:', salaryData)
              setContract((prev) => ({
                ...prev,
                basicSalary: salaryData.basicSalary ?? prev.basicSalary,
                housingAllowance: salaryData.housingAllowance ?? prev.housingAllowance,
                transpotationAllowance: salaryData.transportationAllowance ?? prev.transpotationAllowance,
                mealAllowance: salaryData.mealAllowance ?? prev.mealAllowance,
                gosiDeduction: salaryData.gosiDeduction ?? prev.gosiDeduction,
                additionalJobSalary: salaryData.additionalJobSalary ?? prev.additionalJobSalary,
                otherDeductions: salaryData.otherDeductions ?? prev.otherDeductions,
              }))
            }
          } catch (salaryError: any) {
            console.warn('Failed to load salary data (may not exist yet):', salaryError)
            // Don't show error - salary might not exist for this contract yet
          }
        }
      } catch (error: any) {
        console.error('Failed to load contract', error)
        toast.error(error.response?.data?.message || 'Failed to load contract data')
        backToList()
      } finally {
        setLoading(false)
      }
    }

    loadContract()
  }, [isEditMode, branchId, id, backToList])

  // When leave policy is accessible, fetch leave balances from policy immediately.
  useEffect(() => {
    if (!branchId) return
    if (!contract.leavePolicyAccessible) return
    if (!contract.empId) return
    let cancelled = false
    void contractService
      .getLeaveBalances(contract.empId, branchId)
      .then((lb) => {
        if (cancelled) return
        setLeaveBalances(lb)
        if (!lb) return
        setContract((prev) => ({
          ...prev,
          sickLeavesEntitlement: lb.sick?.entitlement ?? prev.sickLeavesEntitlement,
          sickLeavesAvailed: lb.sick?.availed ?? prev.sickLeavesAvailed,
          sickLeavesBalance: lb.sick?.balance ?? prev.sickLeavesBalance,
          casualLeavesEntitlement: lb.casual?.entitlement ?? prev.casualLeavesEntitlement,
          casualLeavesAvailed: lb.casual?.availed ?? prev.casualLeavesAvailed,
          casualLeavesBalance: lb.casual?.balance ?? prev.casualLeavesBalance,
          annualLeavesEntitlement: lb.annual?.entitlement ?? prev.annualLeavesEntitlement,
          leavesAvailed: lb.annual?.availed ?? prev.leavesAvailed,
          leavesBalance: lb.annual?.balance ?? prev.leavesBalance,
          leaveAccuralRate: lb.annual?.accrualRate ?? prev.leaveAccuralRate,
        }))
      })
      .catch(() => {
        if (!cancelled) setLeaveBalances(null)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, contract.empId, contract.leavePolicyAccessible])

  // Auto-calculate contract months from start/end dates.
  useEffect(() => {
    if (!contract.contractStartDate || !contract.contractEndDate) return
    const start = new Date(contract.contractStartDate)
    const end = new Date(contract.contractEndDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return
    if (end < start) return
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      (end.getDate() >= start.getDate() ? 1 : 0)
    const safeMonths = Math.max(0, months)
    if (safeMonths !== (Number(contract.contractMonths) || 0)) {
      setContract((prev) => ({ ...prev, contractMonths: safeMonths }))
    }
  }, [contract.contractStartDate, contract.contractEndDate])

  // Calculate Total Salary (Basic Salary + all allowances + acting job salary - GOSI Deduction)
  useEffect(() => {
    const grossSalary =
      (Number(contract.basicSalary) || 0) +
      (Number(contract.housingAllowance) || 0) +
      (Number(contract.transpotationAllowance) || 0) +
      (Number(contract.mealAllowance) || 0) +
      (Number(contract.additionalJobSalary) || 0)
    const netSalary = grossSalary - (Number(contract.gosiDeduction) || 0)
    setContract((prev) => ({ ...prev, totalContractSalary: netSalary }))
  }, [
    contract.basicSalary,
    contract.housingAllowance,
    contract.transpotationAllowance,
    contract.mealAllowance,
    contract.additionalJobSalary,
    contract.gosiDeduction,
  ])

  // Calculate leave balances
  useEffect(() => {
    if (contract.leavePolicyAccessible) return
    const annualBalance = (contract.annualLeavesEntitlement || 0) - (contract.leavesAvailed || 0)
    const casualBalance = (contract.casualLeavesEntitlement || 0) - (contract.casualLeavesAvailed || 0)
    const sickBalance = (contract.sickLeavesEntitlement || 0) - (contract.sickLeavesAvailed || 0)

    setContract((prev) => ({
      ...prev,
      leavesBalance: annualBalance,
      casualLeavesBalance: casualBalance,
      sickLeavesBalance: sickBalance,
    }))
  }, [
    contract.leavePolicyAccessible,
    contract.annualLeavesEntitlement,
    contract.leavesAvailed,
    contract.casualLeavesEntitlement,
    contract.casualLeavesAvailed,
    contract.sickLeavesEntitlement,
    contract.sickLeavesAvailed,
  ])

  // Prepare options for dropdowns
  const employeeOptions: SearchableSelectOption[] = useMemo(() => {
    console.log('🔵 [ContractDetail] Preparing employee options from:', employees.length, 'employees')
    if (employees.length === 0) {
      console.warn('⚠️ [ContractDetail] No employees available for dropdown')
    } else {
      console.log('🔵 [ContractDetail] First employee sample:', employees[0])
    }
    const options = employees.map((emp) => {
      // Use fullName if available, otherwise construct from firstName, secondName, lastName
      const fullName = emp.fullName || 
        [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn]
          .filter(Boolean)
          .join(' ')
          .trim() || 
        `Employee ${emp.empId}`
      return {
        value: String(emp.empId),
        label: fullName,
      }
    })
    console.log('✅ [ContractDetail] Created employee options:', options.length)
    return options
  }, [employees])

  const gradeOptions: SearchableSelectOption[] = useMemo(() => {
    console.log('Preparing grade options from:', grades.length, 'grades')
    return grades.map((grade) => {
      const gradeId = grade.id || grade.gradeId || 0
      // Priority: lookupNameEn > lookupNameAr > gradeNameEn > gradeNameAr > gradeName
      const gradeName = grade.lookupNameEn || grade.lookupNameAr || grade.gradeNameEn || grade.gradeNameAr || grade.gradeName || `Grade ${gradeId}`
      return {
        value: String(gradeId),
        label: gradeName,
      }
    })
  }, [grades])

  const projectOptions: SearchableSelectOption[] = useMemo(() => {
    return projects.map((project) => ({
      value: String(project.id || project.projectId || 0),
      label: project.projectNameEn || project.projectNameAr || project.projectName || `Project ${project.id || project.projectId}`,
    }))
  }, [projects])

  const statusOptionsList: SearchableSelectOption[] = useMemo(() => {
    return statusOptions.map((status) => ({
      value: String(status.id),
      label: status.name,
    }))
  }, [statusOptions])

  const yesNoOptions: SearchableSelectOption[] = useMemo(
    () => [
      { value: 'true', label: t('contracts.yes') },
      { value: 'false', label: t('contracts.no') },
    ],
    [t]
  )

  // Gosi type options: by employee nationality (empNationalityId)
  // empNationalityId === 1 → Social (1) + Additional (2). Otherwise → Additional (2) + Non Saudi (3)
  const selectedEmployee = useMemo(
    () => (contract.empId ? employees.find((e) => e.empId === contract.empId) : undefined),
    [contract.empId, employees]
  )
  const gosiTypeOptions: SearchableSelectOption[] = useMemo(() => {
    const all = [
      { value: '1', label: 'Social security percent' },
      { value: '2', label: 'Additional social percent' },
      { value: '3', label: 'Non Saudi social percent' },
    ]
    if (!selectedEmployee) return all
    const natId = selectedEmployee.empNationalityId
    if (natId === 1) return all.filter((o) => o.value === '1' || o.value === '2')
    return all.filter((o) => o.value === '2' || o.value === '3')
  }, [selectedEmployee])

  // When employee changes, keep only gosi types that are still allowed
  useEffect(() => {
    const allowed = gosiTypeOptions.map((o) => Number(o.value))
    const current = contract.gosiType ?? []
    const kept = current.filter((v) => allowed.includes(v))
    if (kept.length !== current.length) {
      setContract((prev) => ({ ...prev, gosiType: kept }))
    }
  }, [selectedEmployee?.empId, gosiTypeOptions, contract.gosiType])

  const handleFieldChange = (field: keyof Contract, value: any) => {
    setContract((prev) => ({ ...prev, [field]: value }))
  }

  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return ''
    try {
      if (dateString.includes('T')) {
        return new Date(dateString).toISOString().split('T')[0]
      }
      return dateString.split('T')[0]
    } catch {
      return dateString
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError('Please select a branch first')
      return
    }

    if (!contract.empId || contract.empId === 0) {
      await showValidationError('Please select an employee')
      return
    }

    // Department validation removed - now optional

    if (!contract.projectId || contract.projectId === 0) {
      await showValidationError('Please select a project')
      return
    }

    if (!contract.contractStartDate) {
      await showValidationError('Please enter contract start date')
      return
    }

    if (!contract.contractDate) {
      await showValidationError('Please enter contract date')
      return
    }

    if (contract.contractEndDate) {
      const start = new Date(contract.contractStartDate)
      const end = new Date(contract.contractEndDate)
      if (start > end) {
        await showValidationError('Contract start date cannot be greater than contract end date')
        return
      }
      const contractDate = new Date(contract.contractDate)
      if (contractDate > end) {
        await showValidationError('Contract date cannot be greater than contract end date')
        return
      }
    }

    if (!contract.probationPeriod && contract.probationPeriod !== 0) {
      await showValidationError('Please enter probation period')
      return
    }

    if (!contract.workingDays && contract.workingDays !== 0) {
      await showValidationError('Please enter working days')
      return
    }

    if (!contract.basicSalary && contract.basicSalary !== 0) {
      await showValidationError('Please enter basic salary')
      return
    }

    if (!contract.housingAllowance && contract.housingAllowance !== 0) {
      await showValidationError('Please enter housing allowance')
      return
    }

    if (!contract.mealAllowance && contract.mealAllowance !== 0) {
      await showValidationError('Please enter meal allowance')
      return
    }


    try {
      setSaving(true)
      showLoading(
        isEditMode ? 'Updating Contract...' : 'Saving Contract...',
        'Please wait while we process your request'
      )

      const contractToSave: Contract = {
        ...contract,
        branchId: branchId,
      }

      if (isEditMode) {
        await contractService.updateContract(contractToSave, branchId)
      } else {
        await contractService.saveContract(contractToSave, branchId)
      }

      closeSwal()
      await showSaveSuccess(
        isEditMode,
        isEditMode ? 'Contract updated successfully' : 'Contract created successfully'
      )

      backToList(contract.empId)
    } catch (error: any) {
      console.error('Failed to save contract', error)
      closeSwal()
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const handleRenew = async () => {
    if (!branchId) {
      await showValidationError('Please select a branch')
      return
    }
    if (!contract.empId) {
      await showValidationError('Please select an employee')
      return
    }
    try {
      setRenewing(true)
      showLoading('Renewing Contract...', 'Please wait')
      const contractToRenew: Contract = { ...contract, branchId }
      await contractService.renewContract(contractToRenew, branchId)
      closeSwal()
      await showSaveSuccess(true, 'Contract renewed successfully')
      if (id && id !== 'new') {
        const data = await contractService.getContractById(Number(id), branchId)
        setContract(data)
      }
    } catch (error: any) {
      closeSwal()
      await showSaveError(error, true)
    } finally {
      setRenewing(false)
    }
  }

  if (loading && !contract.empId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('contracts.title')}
        description={isEditMode ? t('contracts.updateContractDetails') : t('contracts.createNewEmployeeContract')}
      />

      {/* Contract Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('contracts.contract')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Emp Code */}
            <div className="space-y-2">
              <Label>{t('contracts.empCode', { defaultValue: 'Emp #' })}</Label>
              <Input
                readOnly
                value={(selectedEmployee?.empCode ?? '').trim() || '—'}
                className="bg-muted"
                aria-readonly
              />
            </div>
            {/* Employee Dropdown */}
            <div className="space-y-2">
              <Label>
                {t('contracts.employee')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={employeeOptions}
                value={contract.empId ? String(contract.empId) : ''}
                onValueChange={(value) => handleFieldChange('empId', Number(value))}
                placeholder={t('contracts.selectEmployee')}
                searchPlaceholder={t('contracts.searchEmployees')}
                emptyMessage={t('contracts.noEmployeesFound')}
                disabled={saving || isEditMode || listEmpId > 0}
              />
            </div>

            {/* Grade */}
            <div className="space-y-2">
              <Label>{t('contracts.grade')}</Label>
              <SearchableSelect
                options={gradeOptions}
                value={contract.gradeId ? String(contract.gradeId) : ''}
                onValueChange={(value) => handleFieldChange('gradeId', Number(value))}
                placeholder={t('contracts.selectGrade')}
                searchPlaceholder={t('contracts.searchGrades')}
                emptyMessage={t('contracts.noGradesFound')}
                disabled={saving}
              />
            </div>

            {/* Contract Date - first */}
            <div className="space-y-2">
              <Label>
                {t('contracts.contractDate')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={formatDateForInput(contract.contractDate)}
                onChange={(e) => handleFieldChange('contractDate', e.target.value)}
                disabled={saving}
              />
              {contract.contractStartDate && contract.contractDate && new Date(contract.contractDate) < new Date(contract.contractStartDate) && (
                <p className="text-sm text-destructive font-medium">Contract date cannot be before start date</p>
              )}
              {contract.contractEndDate && contract.contractDate && new Date(contract.contractDate) > new Date(contract.contractEndDate) && (
                <p className="text-sm text-destructive font-medium">Contract date cannot be after end date</p>
              )}
              {contract.contractEndDate && (() => {
                const end = new Date(contract.contractEndDate)
                end.setHours(0, 0, 0, 0)
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return end.getTime() <= today.getTime()
              })() && (
                <p className="text-sm text-destructive font-medium">Contract expired</p>
              )}
            </div>

            {/* Contract Start Date */}
            <div className="space-y-2">
              <Label>
                {t('contracts.contractStartDate')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={formatDateForInput(contract.contractStartDate)}
                onChange={(e) => handleFieldChange('contractStartDate', e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Contract End Date */}
            <div className="space-y-2">
              <Label>{t('contracts.contractEndDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(contract.contractEndDate)}
                onChange={(e) => handleFieldChange('contractEndDate', e.target.value)}
                disabled={saving}
              />
              {contract.contractStartDate && contract.contractEndDate && new Date(contract.contractEndDate) < new Date(contract.contractStartDate) && (
                <p className="text-sm text-destructive font-medium">End date cannot be before start date</p>
              )}
            </div>

            {/* Contract Months */}
            <div className="space-y-2">
              <Label>{t('contracts.contractMonths')}</Label>
              <Input
                type="number"
                value={contract.contractMonths ?? ''}
                onChange={(e) => handleFieldChange('contractMonths', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Project */}
            <div className="space-y-2">
              <Label>
                {t('contracts.project')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={projectOptions}
                value={contract.projectId ? String(contract.projectId) : ''}
                onValueChange={(value) => handleFieldChange('projectId', Number(value))}
                placeholder={t('contracts.selectProject')}
                searchPlaceholder={t('contracts.searchProjects')}
                emptyMessage={t('contracts.noProjectsFound')}
                disabled={saving}
              />
            </div>

            {/* Gosi Type – options by employee nationality: Saudi (1) → Social + Additional; else → Additional + Non Saudi */}
            <div className="space-y-2">
              <Label>Gosi type</Label>
              <SearchableMultiSelect
                options={gosiTypeOptions}
                value={contract.gosiType?.map(String) || []}
                onValueChange={(values) => handleFieldChange('gosiType', values.map(Number))}
                placeholder="Select Gosi type..."
                searchPlaceholder="Search..."
                emptyMessage="No options found"
                disabled={saving}
              />
            </div>

            {/* Probation Period */}
            <div className="space-y-2">
              <Label>
                {t('contracts.probationPeriod')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={contract.probationPeriod || ''}
                onChange={(e) => handleFieldChange('probationPeriod', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Working Days */}
            <div className="space-y-2">
              <Label>
                {t('contracts.workingDays')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={contract.workingDays || ''}
                onChange={(e) => handleFieldChange('workingDays', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Status - last */}
            <div className="space-y-2">
              <Label>
                {t('contracts.status')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={statusOptionsList}
                value={contract.contractstatus !== undefined ? String(contract.contractstatus) : ''}
                onValueChange={(value) => handleFieldChange('contractstatus', Number(value))}
                placeholder={t('contracts.selectStatus')}
                searchPlaceholder={t('contracts.searchStatus')}
                emptyMessage={t('contracts.noStatusFound')}
                disabled={saving}
              />
            </div>

            {/* Leave Policy Accessible */}
            <div className="space-y-2">
              <Label>{t('contracts.leavePolicyAccessible')}</Label>
              <SearchableSelect
                options={yesNoOptions}
                value={String(!!contract.leavePolicyAccessible)}
                onValueChange={(value) => handleFieldChange('leavePolicyAccessible', value === 'true')}
                placeholder={t('contracts.selectType')}
                searchPlaceholder={t('contracts.searchTypes')}
                emptyMessage={t('contracts.noTypesFound')}
                disabled={saving}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leave Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('contracts.leave')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Annual (API) */}
            <div className="space-y-2">
              <Label>{t('contracts.annualLeavesEntitlement')}</Label>
              <Input type="number" value={leaveBalances?.annual?.entitlement ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.leavesAvailed')}</Label>
              <Input type="number" value={leaveBalances?.annual?.availed ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.leavesBalance')}</Label>
              <Input type="number" value={leaveBalances?.annual?.balance ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.leaveAccuralRate')}</Label>
              <Input type="number" step="0.01" value={leaveBalances?.annual?.accrualRate ?? ''} readOnly className="bg-muted" />
            </div>

            {/* Sick (API) */}
            <div className="space-y-2">
              <Label>{t('contracts.sickLeavesEntitlement')}</Label>
              <Input type="number" value={leaveBalances?.sick?.entitlement ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.sickLeavesAvailed')}</Label>
              <Input type="number" value={leaveBalances?.sick?.availed ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.sickLeavesBalance')}</Label>
              <Input type="number" value={leaveBalances?.sick?.balance ?? ''} readOnly className="bg-muted" />
            </div>

            {/* Casual (API) */}
            <div className="space-y-2">
              <Label>{t('contracts.casualLeavesEntitlement')}</Label>
              <Input type="number" value={leaveBalances?.casual?.entitlement ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.casualLeavesAvailed')}</Label>
              <Input type="number" value={leaveBalances?.casual?.availed ?? ''} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('contracts.casualLeavesBalance')}</Label>
              <Input type="number" value={leaveBalances?.casual?.balance ?? ''} readOnly className="bg-muted" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Salary Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('contracts.salarySection')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Basic Salary */}
            <div className="space-y-2">
              <Label>
                {t('contracts.basicSalary')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={contract.basicSalary || 0}
                onChange={(e) => handleFieldChange('basicSalary', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Housing Allowance */}
            <div className="space-y-2">
              <Label>
                {t('contracts.housingAllowance')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={contract.housingAllowance || 0}
                onChange={(e) => handleFieldChange('housingAllowance', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Transportation Allowance */}
            <div className="space-y-2">
              <Label>{t('contracts.transportationAllowance')}</Label>
              <Input
                type="number"
                step="0.01"
                value={contract.transpotationAllowance || 0}
                onChange={(e) => handleFieldChange('transpotationAllowance', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Meal Allowance */}
            <div className="space-y-2">
              <Label>
                {t('contracts.mealAllowance')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={contract.mealAllowance || 0}
                onChange={(e) => handleFieldChange('mealAllowance', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* GOSI Deduction */}
            <div className="space-y-2">
              <Label>
                {t('contracts.gosiDeduction')}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={contract.gosiDeduction || 0}
                readOnly
                className="bg-muted"
              />
            </div>

            {/* Acting job */}
            <div className="space-y-2">
              <Label>{t('contracts.additionalJob')}</Label>
              <Input
                type="text"
                value={contract.additionalJob ?? ''}
                onChange={(e) => handleFieldChange('additionalJob', e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Acting job salary */}
            <div className="space-y-2">
              <Label>{t('contracts.additionalJobSalary')}</Label>
              <Input
                type="number"
                step="0.01"
                value={contract.additionalJobSalary ?? 0}
                onChange={(e) => handleFieldChange('additionalJobSalary', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>

            {/* Additional Job Expiry */}
            <div className="space-y-2">
              <Label>{t('contracts.additionalJobExpiry')}</Label>
              <Input
                type="date"
                value={formatDateForInput(contract.additionalJobExpiry)}
                onChange={(e) => handleFieldChange('additionalJobExpiry', e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Total Salary (after GOSI deduction) */}
            <div className="space-y-2">
              <Label>Total Salary</Label>
              <Input
                type="number"
                step="0.01"
                value={contract.totalContractSalary || 0}
                readOnly
                className="bg-muted font-semibold"
              />
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/contracts')}
          className="w-full sm:w-auto"
          disabled={saving || renewing}
        >
          {t('contracts.cancel')}
        </Button>
        {showRenewButton && (
          <Button
            type="button"
            variant="outline"
            onClick={handleRenew}
            disabled={saving || renewing || !branchId}
            className="w-full sm:w-auto min-w-[120px]"
          >
            {renewing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Renewing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Renew
              </>
            )}
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving || renewing || !branchId}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isEditMode ? t('contracts.updating') : t('contracts.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('contracts.updateContract') : t('contracts.saveContract')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
